import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { toast } from 'react-hot-toast'
import { IoIosWarning } from 'react-icons/io'
import { IoAddSharp } from 'react-icons/io5'
import { ISession, useDeleteAllSessionsMutation, useGetSessionsQuery } from '../../../features/account/sessionSlice'
import { Loader } from '../../../shared/Loader'
import { RemoveSessionModal } from './RemoveSessionModal'

export const ActiveSessions = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { data: sessions, isLoading, isError } = useGetSessionsQuery()

  const [removeAll, { isLoading: isDeleting }] = useDeleteAllSessionsMutation()

  const handleDelete = async () => {
    try {
      setIsModalOpen(false)
      toast.loading('Deleting sessions...')
      await removeAll().unwrap()
      toast.dismiss()
      toast.success('Sessions removed successfully!')
    } catch (error: any) {
      console.log(error)
      toast.dismiss()
      toast.error(error?.data.message)
    }
  }

  return (
    <div className='w-full relative py-8'>
      {isLoading ? (
        <div className='w-full h-[70vh] flex justify-center items-center'>
          <Loader />
        </div>
      ) : sessions && sessions?.length > 0 ? (
        <div className='w-full md:px-4 py-5 md:py-3 bg-black'>
          <div className='flex items-center justify-between pr-5'>
            <h1 className='text-xl text-primary font-semibold pl-5'>
              Active Sessions
            </h1>

            <button
              className='transition-all duration-200 flex items-center gap-2 rounded-full px-4 py-2 bg-white/90 hover:bg-white hover:text-black text-sm'
              onClick={() => setIsModalOpen(true)}
            >
              <span>
                <IoAddSharp className='text-lg text-black' />
              </span>{' '}
              Logout All Sessions
            </button>
          </div>
          <div className='overflow-x-auto'>
            <table className='relative mt-8 w-full text-sm text-left text-gray-400'>
              <thead className='text-xs uppercase bg-gray-700 text-gray-300'>
                <tr>
                  <th scope='col' className='py-3 px-6'>
                    Session Id
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    User Agent
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Device
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    IP
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Logged In At
                  </th>
                  <th scope='col' className='py-3 px-6'></th>
                </tr>
              </thead>
              <tbody>
                {sessions?.map((session: ISession) => (
                  <tr className='border-b bg-transparent border-gray-700'>
                    <th
                      scope='row'
                      className='py-4 px-6 font-medium whitespace-nowrap text-white'
                    >
                      {session.sessionId}
                    </th>

                    <td className='py-4 px-6'>{session.userAgent}</td>
                    <td className='py-4 px-6'>{session.platform}</td>
                    <td className='py-4 px-6'>{session.clientIp}</td>
                    <td className='py-4 px-6'>{new Date(session.createdAt).toUTCString()}</td>
                    <td>
                      <div className='flex items-center gap-2'>
                        <RemoveSessionModal session={session} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className='max-w-md mx-auto flex justify-center items-center pt-20'>
          <div className='flex flex-col items-center gap-2'>
            {/* <BsBank2 className='box-content p-4 rounded-md text-5xl bg-gray-600 text-white/20' /> */}

            <p className='text-sm text-white/30 font-light text-center'>
              No Sessions Found
            </p>


          </div>
        </div>
      )}

      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <div className='fixed inset-0 overflow-y-auto'>
                  <div className='flex min-h-full items-center justify-center p-4 text-center'>
                    <Transition.Child
                      as={Fragment}
                      enter='ease-out duration-300'
                      enterFrom='opacity-0 scale-95'
                      enterTo='opacity-100 scale-100'
                      leave='ease-in duration-200'
                      leaveFrom='opacity-100 scale-100'
                      leaveTo='opacity-0 scale-95'
                    >
                      <Dialog.Panel className='relative font-dashboard w-full max-w-2xl min-h-[400px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all'>
                        <div className='flex justify-center py-4'>
                          <div className='flex flex-col items-center gap-2'>
                            <IoIosWarning className='text-8xl text-yellow-500' />
                            <p className='text-lg md:text-xl text-center font-light text-white/80'>
                              Are you sure want to logout from all session?
                            </p>
                            <p className='text-sm font-light text-white/50'>
                              You will be logged out from all devices except this one.
                            </p>

                            <div className='flex items-center gap-4 mt-8'>
                              <button
                                className='transition-all duration-200 px-6 py-2 rounded-full text-sm bg-red-500 hover:bg-red-600 text-white'
                                onClick={handleDelete}
                              >
                                Yes, logout from all devices
                              </button>
                              <button
                                className='transition-all duration-200 px-6 py-2 rounded-full text-sm bg-white/80 hover:bg-white text-black'
                                onClick={() => setIsModalOpen(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
